import { Alert, Button, Space, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import { createInventorizationBlockStatuses } from '@/features/inventorization/domain/inventorization';
import { readInventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import { readInventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import { readInventorizationPosition } from '@/features/inventorization/domain/inventorization-position';
import {
  readInventorizationMainReading,
  readInventorizationRegisters,
} from '@/features/inventorization/domain/inventorization-reading';
import { readInventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import { createResolvedInventorizationValueMap } from '@/features/inventorization/domain/review/inventorization-value-resolver';
import {
  createInventorizeTechnicalDetailReviewFieldId,
  INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS,
} from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import type { InventorizeReviewField } from '@/features/inventorization/domain/review/inventorize-review-fields';
import { createInventorizeReviewFields } from '@/features/inventorization/domain/review/inventorize-review-fields';
import { createFieldWizardDefinition } from '@/features/inventorization/fieldWizardRegistry';
import { messages } from '@/features/inventorization/messages';
import { hasCompletedLlmExtraction } from '@/features/inventorization/photos/photo-wizard-steps';
import { useCapturePhotoSlot } from '@/features/inventorization/photos/useCapturePhotoSlot';
import { useInventorizationSession } from '@/features/inventorization/useInventorizationSession';
import { useInventorizeExtractionPolling } from '@/features/inventorization/useInventorizeExtractionPolling';
import { useInventorizeLlmParse } from '@/features/inventorization/useInventorizeLlmParse';
import { useInventorizationActivityTimer } from '@/features/inventorization/useMeterActivityTimer';
import { useQueueInventorizationSession } from '@/features/inventorization/useQueueInventorizationSession';
import { useReviewFieldOptions } from '@/features/inventorization/useReviewFieldOptions';
import { useLocationBootstrap } from '@/features/location-bootstrap/LocationBootstrapContext';
import { InventorizationReviewStage } from '@/routers/inventorization/components/InventorizationReviewStage';
import { InventorizeCommodityStep } from '@/routers/inventorization/components/InventorizeCommodityStep';
import { InventorizeFieldWizard } from '@/routers/inventorization/components/InventorizeFieldWizard';
import { NewMeterLocationStep } from '@/routers/inventorization/components/NewMeterLocationStep';
import {
  hasCompletedInventorizePhotoWizard,
  InventorizePhotoWizard,
} from '@/routers/inventorization/components/photos/InventorizePhotoWizard';
import { GpsPositionPicker } from '@/routers/inventorization/components/position/GpsPositionPicker';
import { summarizeReadiness } from '@/shared/capture/capture-session';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

interface InventorizationViewProps {
  activeFieldId?: string | null;
  onBackToScan: () => void;
  onCloseField: () => void;
  onOpenField: (field: InventorizeReviewField) => void;
  onQueuedToToday: () => void;
  sessionId: string;
}

type NewMeterWizardStep = 'commodity' | 'gps' | 'location' | null;

export function InventorizationView({
  activeFieldId = null,
  onBackToScan,
  onCloseField,
  onOpenField,
  onQueuedToToday,
  sessionId,
}: InventorizationViewProps) {
  const intl = useIntl();
  const { activeLocation } = useStartupSession();
  const { bootstrap } = useLocationBootstrap();
  const [editingStep, setEditingStep] = useState<NewMeterWizardStep>(null);
  const [startedWizardSessionId, setStartedWizardSessionId] = useState<string | null>(null);
  const {
    error,
    isLoading,
    refresh,
    saveContext,
    saveIdentity,
    saveMainReading,
    removeRegisterReading,
    savePosition,
    saveRegisterReading,
    saveTechnicalDetails,
    session,
  } = useInventorizationSession(sessionId);
  const { captureSlot, deletePhoto, error: photoError, isCapturingSlotId } = useCapturePhotoSlot(sessionId);
  const {
    error: llmParseError,
    isSubmitting: isSubmittingLlmParse,
    skipLlmParse,
    submitPhotosForExtraction,
  } = useInventorizeLlmParse(sessionId);
  const { error: queueError, isQueueing, issues, queueOffline } = useQueueInventorizationSession(sessionId);
  const extractionPolling = useInventorizeExtractionPolling({
    onRefresh: refresh,
    session,
  });
  useInventorizationActivityTimer(session);
  const blockStatuses = {
    ...createInventorizationBlockStatuses(),
    ...session?.blockStatuses,
  };
  const readiness = summarizeReadiness(blockStatuses);
  const manufacturerDisplayNames = useMemo(
    () =>
      Object.fromEntries(
        (bootstrap?.manufacturers ?? []).map(manufacturer => [String(manufacturer.id), manufacturer.name]),
      ),
    [bootstrap?.manufacturers],
  );
  const meterModelDisplayNames = useMemo(
    () => Object.fromEntries((bootstrap?.meterModels ?? []).map(model => [String(model.id), model.name])),
    [bootstrap?.meterModels],
  );
  const context = session ? readInventorizationContext(session) : undefined;
  const identity = session ? readInventorizationIdentity(session) : undefined;
  const position = session ? readInventorizationPosition(session) : undefined;
  const reading = session ? readInventorizationMainReading(session) : undefined;
  const registers = session ? readInventorizationRegisters(session) : [];
  const technical = session ? readInventorizationTechnicalDetails(session) : undefined;
  const resolvedValues = useMemo(
    () => (session ? createResolvedInventorizationValueMap(session) : new Map()),
    [session],
  );
  const effectiveManufacturer =
    identity?.manufacturer || resolvedValues.get(INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.manufacturer)?.value || '';
  const ownerType =
    technical?.detail.ownerType ||
    resolvedValues.get(createInventorizeTechnicalDetailReviewFieldId('ownerType'))?.value ||
    '';
  const fieldOptions = useReviewFieldOptions(effectiveManufacturer, identity?.commodity ?? '');
  const reviewFields = session
    ? createInventorizeReviewFields(session, { manufacturerDisplayNames, meterModelDisplayNames })
    : [];
  const activeField = activeFieldId ? reviewFields.find(field => field.id === activeFieldId) : undefined;
  const isExistingBackendMeter = Boolean(session?._id || session?.meterId);
  const shouldStartWizard = session ? readShouldStartWizard(session) : false;
  const hasCompletedPassportWizard = session
    ? shouldStartWizard
      ? hasCompletedLlmExtraction(session)
      : hasCompletedInventorizePhotoWizard(session)
    : false;
  const shouldRunWizard = !isExistingBackendMeter || (shouldStartWizard && !hasCompletedPassportWizard);
  // After the photo wizard, review owns these values; editing one there must not reopen the wizard.
  const isInitialWizard = shouldRunWizard && !hasCompletedPassportWizard;
  const shouldShowLocationStep =
    !activeFieldId && position && shouldRunWizard
      ? editingStep === 'location' || (isInitialWizard && !(position.floor && position.floorplanState))
      : false;
  const shouldShowGpsStep =
    !activeFieldId && position && !shouldShowLocationStep && shouldRunWizard
      ? editingStep === 'gps' || (isInitialWizard && !position.gpsState)
      : false;
  const shouldShowCommodityStep =
    !activeFieldId && identity && !shouldShowLocationStep && !shouldShowGpsStep && shouldRunWizard
      ? editingStep === 'commodity' || (isInitialWizard && !identity.commodity)
      : false;
  const shouldShowPhotoWizard =
    !activeFieldId && session && position && !shouldShowLocationStep && !shouldShowGpsStep && !shouldShowCommodityStep
      ? shouldRunWizard && !hasCompletedPassportWizard
      : false;

  useEffect(() => {
    if (session && shouldStartWizard && shouldRunWizard && startedWizardSessionId !== session.id) {
      setStartedWizardSessionId(session.id);
      setEditingStep('location');
    }
  }, [session, shouldRunWizard, shouldStartWizard, startedWizardSessionId]);

  async function approveReviewField(field: InventorizeReviewField) {
    if (!(context && identity && position && reading && technical)) {
      return;
    }

    const definition = createFieldWizardDefinition({
      buildingOptions: fieldOptions.buildingOptions,
      context,
      field,
      identity,
      manufacturerOptions: fieldOptions.manufacturerOptions,
      meterModels: fieldOptions.meterModels,
      modelOptions: fieldOptions.modelOptions,
      onSaveContext: saveContext,
      onSaveIdentity: saveIdentity,
      onSavePosition: savePosition,
      onSaveReading: saveMainReading,
      onSaveRegisterReading: saveRegisterReading,
      onSaveTechnical: saveTechnicalDetails,
      ownerSuggestions: fieldOptions.ownerSuggestions,
      ownerType,
      position,
      reading,
      registers,
      technical,
      tenantOptions: fieldOptions.tenantOptions,
      utilityCompanyOwnerOptions: fieldOptions.utilityCompanyOwnerOptions,
    });

    if (definition.type === 'readonly') {
      onOpenField(field);
      return;
    }

    await definition.save(field.value);
    await refresh();
  }

  if (isLoading) {
    return (
      <Typography.Text type="secondary">{intl.formatMessage(messages.inventorizationLoadingDraft)}</Typography.Text>
    );
  }

  if (error || !session) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Typography.Text type="secondary">
            {intl.formatMessage(messages.inventorizationDraftUnavailable)}
          </Typography.Text>
          <Typography.Title level={3} style={{ marginTop: 0 }}>
            {intl.formatMessage(messages.inventorizationCannotOpen)}
          </Typography.Title>
        </div>
        <Alert message={error ?? intl.formatMessage(messages.inventorizationDraftNotFound)} showIcon type="error" />
        <Button onClick={onBackToScan}>{intl.formatMessage(messages.inventorizationActionsBackToScan)}</Button>
      </Space>
    );
  }

  if (shouldShowLocationStep && position) {
    return (
      <NewMeterLocationStep
        onBack={onBackToScan}
        onNext={async patch => {
          await savePosition(patch);
          await saveContext({
            buildingName: patch.building,
            floorName: patch.floor,
            locationName: activeLocation?.locationName ?? readInventorizationContext(session).locationName,
          });
          setEditingStep('gps');
        }}
        position={position}
      />
    );
  }

  if (shouldShowGpsStep && position) {
    return (
      <GpsPositionPicker
        backLabel={intl.formatMessage(messages.appCommonBack)}
        description={translateInventoryText(
          'app.inventory.position.gps.description',
          'Use current GPS, tap the map to place this meter, or drag the marker to refine it. Grey pins are other cached meters in this location.',
        )}
        onBack={() => {
          setEditingStep('location');
        }}
        onSave={patch =>
          void (async () => {
            await savePosition(patch);
            setEditingStep('commodity');
          })()
        }
        position={position}
        saveLabel={intl.formatMessage(messages.appCommonNext)}
        section={intl.formatMessage(messages.inventorizationNewMeter)}
        title={translateInventoryText('app.inventory.position.gps.mapPosition', 'Map position')}
      />
    );
  }

  if (shouldShowCommodityStep && identity) {
    return (
      <InventorizeCommodityStep
        commodity={identity.commodity}
        onBack={() => {
          setEditingStep('gps');
        }}
        onNext={async commodity => {
          await saveIdentity({ commodity });
          setEditingStep(null);
        }}
      />
    );
  }

  if (shouldShowPhotoWizard) {
    return (
      <InventorizePhotoWizard
        error={photoError ?? llmParseError}
        isCapturingSlotId={isCapturingSlotId}
        isSubmittingLlm={isSubmittingLlmParse}
        onBack={() => setEditingStep('commodity')}
        onCapture={captureSlot}
        onContinueWithoutLlm={photoIds => skipLlmParse(photoIds, 'manual_continue_without_llm')}
        onDelete={deletePhoto}
        onRefresh={refresh}
        onSaveTechnical={saveTechnicalDetails}
        onSubmitLlm={submitPhotosForExtraction}
        session={session}
        technical={readInventorizationTechnicalDetails(session)}
      />
    );
  }

  if (activeField) {
    return (
      <InventorizeFieldWizard
        context={readInventorizationContext(session)}
        field={activeField}
        identity={readInventorizationIdentity(session)}
        isCapturingSlotId={isCapturingSlotId}
        onBack={onCloseField}
        onCapturePhoto={captureSlot}
        onDeletePhoto={deletePhoto}
        onRefresh={refresh}
        onSaveContext={saveContext}
        onSaveIdentity={saveIdentity}
        onSavePosition={savePosition}
        onSaveReading={saveMainReading}
        onSaveRegisterReading={saveRegisterReading}
        onSaveTechnical={saveTechnicalDetails}
        position={readInventorizationPosition(session)}
        reading={readInventorizationMainReading(session)}
        registers={readInventorizationRegisters(session)}
        session={session}
        technical={readInventorizationTechnicalDetails(session)}
      />
    );
  }

  return (
    <InventorizationReviewStage
      activeFieldId={activeFieldId}
      extractionError={extractionPolling.error}
      fields={reviewFields}
      isExtractionPolling={extractionPolling.isPolling}
      isQueueing={isQueueing}
      issues={issues}
      onCapturePhoto={captureSlot}
      onApproveField={field => void approveReviewField(field)}
      onCloseField={onCloseField}
      onDeletePhoto={deletePhoto}
      onOpenField={onOpenField}
      onQueue={queueOffline}
      onQueuedToToday={onQueuedToToday}
      onRefresh={refresh}
      onRemoveRegisterReading={removeRegisterReading}
      onSaveRegisterReading={saveRegisterReading}
      queueError={queueError}
      readiness={readiness}
      session={session}
    />
  );
}

function readShouldStartWizard(session: { data: Record<string, unknown> }) {
  return (session.data.inventorization as { startWizard?: unknown } | undefined)?.startWizard === true;
}
